import { ReactNode, createContext, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { createThemeVersion } from '../../functions/API';
import { addElement } from '../../functions/launcherElementFunctions/addElement';
import { getElementById } from '../../functions/launcherElementFunctions/getElementById';
import { recursivelyRandomizeIDs } from '../../functions/launcherElementFunctions/recursivelyRandomizeIDs';
import { removeElement } from '../../functions/launcherElementFunctions/removeElement';
import { div2ImgDataURL } from '../../functions/utils/div2ImgDataURL';
import {
  HistoryState,
  LauncherVersionData,
  SceneCtxData,
} from '../../types/hooks';
import {
  ILauncherWindow,
  LauncherStateProps,
  LauncherStates,
} from '../../types/launcher';
import {
  LauncherElement,
  LauncherElementTypeName,
} from '../../types/launcherElement';
import { useEditor } from '../useEditor';
import { useElements } from '../useElements';

import newUIThemeJSON from '../../defaultThemeConfigs/NewUITheme.json';

export const SceneCtx = createContext<SceneCtxData | null>(null);

export const SceneProvider = ({ children }: { children: ReactNode }) => {
  const editor = useEditor();
  const [launcherStates, setLauncherStates] = useState<LauncherStates>({
    doesAppDirExist: false,
    isAppInstalled: false,
    doesAppNeedRepair: false,
    isAppUpdateAvailable: false,
    doesAppOngoingTaskExist: false,
    doesAppOngoingInstallTaskExist: false,
    doesAppOngoingUpdateTaskExist: false,
    doesAppOngoingUninstallTaskExist: false,
    doesAppOngoingRepairTaskExist: false,
    doesAppOngoingMoveTaskExist: false,
    doesAppLastTaskFailed: false,
  });
  const [selectedElementIDs, setSelectedElementIDs] = useState<string[]>([]);
  const [launcherWindow, setLauncherWindow] = useState<ILauncherWindow>(
    {} as ILauncherWindow
  );
  const [offset, setOffset] = useState<{ x: number; y: number }>({
    x: 0,
    y: 0,
  });
  const [historyUndoStack, setHistoryUndoStack] = useState<HistoryState[]>([]);
  const [historyRedoStack, setHistoryRedoStack] = useState<HistoryState[]>([]);
  const isHistoryInProgress = useRef(false);
  const [lastHistoryState, setLastHistoryState] = useState<HistoryState | null>(
    null
  );
  const { launcherThemeSession, setLauncherThemeSession } = useEditor();

  const launcherStateProps: LauncherStateProps = {
    ['does-app-dir-exist']: launcherStates?.doesAppDirExist.toString(),
    ['is-app-installed']: launcherStates?.isAppInstalled.toString(),
    ['does-app-need-repair']: launcherStates?.doesAppNeedRepair.toString(),
    ['is-app-update-available']:
      launcherStates?.isAppUpdateAvailable.toString(),
    ['does-app-ongoing-task-exist']:
      launcherStates?.doesAppOngoingTaskExist.toString(),
    ['does-app-ongoing-install-task-exist']:
      launcherStates?.doesAppOngoingInstallTaskExist.toString(),
    ['does-app-ongoing-update-task-exist']:
      launcherStates?.doesAppOngoingUpdateTaskExist.toString(),
    ['does-app-ongoing-uninstall-task-exist']:
      launcherStates?.doesAppOngoingUninstallTaskExist.toString(),
    ['does-app-ongoing-repair-task-exist']:
      launcherStates?.doesAppOngoingRepairTaskExist.toString(),
    ['does-app-ongoing-move-task-exist']:
      launcherStates?.doesAppOngoingMoveTaskExist.toString(),
    ['does-app-last-task-failed']:
      launcherStates?.doesAppLastTaskFailed.toString(),
  };

  const [elements, setElements] = useElements();

  const exportLauncherConfigJSON = () => {
    const configObj: any = launcherWindow;
    configObj.children = elements;

    return JSON.stringify(configObj);
  };

  const importLauncherConfigJSON = (config: string) => {
    const launcherConfig = JSON.parse(config);
    // Init scene window
    setLauncherWindow({
      formatVersion: launcherConfig.formatVersion,
      windowWidth: launcherConfig.windowWidth,
      windowHeight: launcherConfig.windowHeight,
      isWindowResizable: false,
      class: launcherConfig.class,
    });
    // Init scene elements
    const childrenArr = launcherConfig.children as LauncherElement[];
    setElements(childrenArr);
  };

  const deleteSelectedElements = () => {
    let newElements = elements;
    selectedElementIDs.forEach((id) => {
      [newElements] = removeElement(newElements, id);
    });
    setElements(newElements);
    setSelectedElementIDs([]);
  };

  const saveThemeVersion = async () => {
    const themeID = launcherThemeSession.themeID;
    if (!themeID) return;

    const thumbnail = await toast.promise(div2ImgDataURL('launcherWindow'), {
      pending: 'Generating thumbnail',
      success: 'Thumbnail generated',
      error: 'Error while generating thumbnail',
    });

    const newThemeVersion: LauncherVersionData = await toast.promise(
      createThemeVersion({
        themeID,
        themeConfig: exportLauncherConfigJSON(),
        thumbnail,
      }),
      {
        pending: 'Saving the theme version',
        success: 'Theme version saved',
        error: 'Error while saving the version',
      }
    );
    const { id, createdAt, thumbnailUrl } = newThemeVersion;

    setLauncherThemeSession((themeData) => ({
      ...themeData,
      versions: [{ id, createdAt, thumbnailUrl }, ...themeData.versions],
    }));

    localStorage.removeItem('lastHistoryStateJSON');
  };

  const newTheme = () => {
    importLauncherConfigJSON(
      JSON.stringify({
        formatVersion: '1.0.0',
        windowWidth: 800,
        windowHeight: 600,
        isWindowResizable: false,
        class: {
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: 'white',
          fontFamily: 'Inter',
          overflow: 'hidden',
          color: '#333333',
          fontWeight: 300,
          fontSize: '14px',
          ['*']: {
            transition: 'all 0.3s ease',
          },
          ['::-webkit-scrollbar']: { width: '6px', height: '6px' },
          ['::-webkit-scrollbar-track']: {
            backgroundColor: '#00000080',
            borderRadius: '3px',
          },
          ['::-webkit-scrollbar-corner']: { background: 'transparent' },
          ['::-webkit-scrollbar-thumb']: {
            borderRadius: '3px',
            backgroundColor: '#ffffff80',
          },
        },
        children: [],
      })
    );

    setSelectedElementIDs([]);
  };

  const loadDemoTheme = () => {
    importLauncherConfigJSON(JSON.stringify(newUIThemeJSON));
    setSelectedElementIDs([]);
  };

  const historySave = () => {
    const newState = {
      themeID: editor.launcherThemeSession.themeID,
      launcherConfigJSON: exportLauncherConfigJSON(),
    };

    if (JSON.parse(newState.launcherConfigJSON)?.formatVersion === undefined)
      return;

    setHistoryUndoStack((prev) => [...prev, newState]);
    setHistoryRedoStack([]);

    if (historyUndoStack.length > 0) setLastHistoryState(newState);

    if (historyUndoStack.length >= 1000) {
      setHistoryUndoStack((prev) => prev.slice(1));
    }
  };

  const historyUndo = () => {
    const lastState = historyUndoStack[historyUndoStack.length - 1];
    const preLastState = historyUndoStack[historyUndoStack.length - 2];

    if (preLastState) {
      isHistoryInProgress.current = true;
      importLauncherConfigJSON(preLastState.launcherConfigJSON);
      setHistoryRedoStack((prev) => [...prev, lastState]);
      setHistoryUndoStack((prev) => prev.slice(0, -1));
    }
  };

  const historyRedo = () => {
    const lastState = historyRedoStack[historyRedoStack.length - 1];

    if (lastState) {
      isHistoryInProgress.current = true;
      importLauncherConfigJSON(lastState.launcherConfigJSON);
      setHistoryUndoStack((prev) => [...prev, lastState]);
      setHistoryRedoStack((prev) => prev.slice(0, -1));
    }
  };

  const duplicateSelectedElement = () => {
    const lastSelectedElementID = selectedElementIDs.slice(-1)[0];

    if (lastSelectedElementID) {
      const [element, parent] = getElementById(elements, lastSelectedElementID);
      if (!element) return;

      const elementData = JSON.parse(JSON.stringify(element));

      recursivelyRandomizeIDs(element);

      const [newElements] = addElement({
        elements,
        targetId: parent?.id,
        elementData,
      });

      setElements(newElements);
      setSelectedElementIDs([element.id]);
    }
  };

  const addLauncherElement = (elementType: LauncherElementTypeName) => {
    if (selectedElementIDs.length <= 1) {
      const lastSelectedElementID = selectedElementIDs.slice(-1)[0];
      const [newElements, newElement] = addElement({
        elements,
        elementType,
        targetId: lastSelectedElementID,
      });
      if (!newElement) return;

      setElements(newElements);
      setSelectedElementIDs([newElement.id]);
    } else if (selectedElementIDs.length > 1) {
      // TODO: Multiselect
    }
  };

  // Add history state on Elements change
  useEffect(() => {
    const debouncingTimeMs = 300;

    if (isHistoryInProgress.current) {
      isHistoryInProgress.current = false;
      return;
    }

    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    if (timeoutId !== null) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }

    timeoutId = setTimeout(() => {
      historySave();

      timeoutId = null;
    }, debouncingTimeMs);

    return () => {
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
      }
    };
  }, [elements, launcherWindow]);

  return (
    <SceneCtx.Provider
      value={{
        offset,
        setOffset,

        launcherStates,
        setLauncherStates,
        launcherStateProps,

        selectedElementIDs,
        setSelectedElementIDs,

        launcherWindow,
        setLauncherWindow,

        exportLauncherConfigJSON,
        importLauncherConfigJSON,

        addLauncherElement,
        deleteSelectedElements,
        duplicateSelectedElement,

        saveThemeVersion,
        newTheme,
        loadDemoTheme,

        historySave,
        historyUndo,
        historyRedo,
        lastHistoryState,
      }}
    >
      {children}
    </SceneCtx.Provider>
  );
};
